/**
 * viewportViewFrame — the ONE reading of "which WCS model point sits at the
 * centre of this viewport's window".
 *
 * # The DXF relation
 *
 * A VIEWPORT entity (and the VPORT table record, which uses the same group
 * codes) stores its view centre in the DISPLAY coordinate system:
 *
 *   * `12`/`22` — **view centre point, in DCS**;
 *   * `17`/`27`/`37` — **view target point, in WCS**;
 *   * `16`/`26`/`36` — view direction (WCS), `51` — view twist angle.
 *
 * The DCS origin IS the view target. For a PLAN view (`viewDirection ≈ +Z`)
 * the DCS XY plane is PARALLEL to the WCS XY plane but its axes are TURNED by
 * the view twist (group 51), so the WCS point shown at the centre of the
 * window is
 *
 *     wcsCentre = target.xy + R(twist)·(12, 22)
 *
 * which for the overwhelmingly common `twist = 0` degenerates to the per-axis
 * SUM `target.xy + (12, 22)`. An ABSENT target is the WCS origin, i.e. the
 * rotated 12/22 pair alone. The untwisted sum is character-for-character the
 * relation `dxfBridge.import`'s `extractSavedViewFromRawTableGroups` applies
 * to the VPORT table record's identical group codes; it lives here so the
 * VIEWPORT entity's consumers read it instead of re-deriving it (or, as they
 * did, omitting it). The rotation is derived from `ezdxf` in the twist
 * section below.
 *
 * # Why this is not the raw 12/22 pair
 *
 * PR #794 briefly read 12/22 as already-WCS. It is not, and the argument that
 * settled it is a MEASUREMENT on the reporting file (AC1032, AutoCAD 2018),
 * recorded in full in `dxfBridge.savedViewTarget.property.test.ts`: the file
 * carries 19 paper-space VIEWPORT entities, which use these same group codes
 * and must by construction frame real model content. Of the five whose target
 * is non-zero — the only ones that discriminate — the SUMMED centre frames
 * 50 / 23 / 210 / 41 / 25 non-text model entities while the raw 12/22 centre
 * frames 0 / 0 / 8 / 0 / 0. Five out of five for the sum. That measurement is
 * about VIEWPORT ENTITIES specifically, so it settles this reading directly,
 * not just the VPORT record's.
 *
 * Every DXF fixture in this repo is ezdxf-written and ezdxf's
 * `set_modelspace_vport` calls `reset_wcs()`, which zeroes the target — so
 * this axis is invisible to them and a defect on it hides.
 *
 * # The additive-identity trap
 *
 * An absent target is the WCS ORIGIN, so its additive identity is `0` — NOT
 * the view centre. `buildViewportModelCamera`'s 3-D branch legitimately reads
 * `viewTarget?.x ?? viewCenter.x` for its lookAt PIVOT (a fallback pivot, not
 * a summand); reusing that fallback here would double the centre. Hence the
 * explicit `?? 0` below.
 *
 * # The twist: the SAME rotation applies to the centre and to the offsets
 *
 * The DCS X axis is "horizontal to the right of the screen" (Autodesk,
 * `trans`'s DCS code 2), so for `viewTwistAngle ≠ 0` the DCS basis is ROTATED
 * relative to WCS and the 12/22 pair must be rotated before it is added to the
 * target. PR #794 briefly shipped the plain sum for the twisted case too, on
 * the argument that no measurement in this repo discriminated the rotation.
 * The `ezdxf` reference implementation (CLAUDE.md source of truth #3) does
 * discriminate it. `ezdxf` 1.4.4, `entities/viewport.py`:
 *
 *   * `get_modelspace_limits()` takes a CLOSED FORM only when
 *     `view_twist_angle` is zero, and that closed form is
 *     `Vec3(view_center_point) + Vec3(view_target_point)` — the plain sum,
 *     which is exactly what the untwisted branch below still computes.
 *   * For a non-zero twist it refuses the closed form and defers to
 *     `_get_modelspace_slow()`, which inverts `get_transformation_matrix()`:
 *
 *         m  = translate(-view_target_point)
 *         m @= scale(s)                      # s = vp.height / view_height
 *         if rotation_angle: m @= z_rotate(radians(rotation_angle))
 *         m @= translate(offset)             # offset = vp.center - viewCenter*s
 *
 * `ezdxf`'s `Matrix44` composes left-to-right, so the forward (model→paper)
 * map is `p_paper = R(θ)·s·(p − target) + (vp.center − s·viewCenter)`.
 * Inverting it and evaluating at the window centre `p_paper = vp.center`:
 *
 *     p       = target + (1/s)·R(−θ)·(p_paper − vp.center) + R(−θ)·viewCenter
 *     p_centre= target + R(−θ)·viewCenter
 *
 * The load-bearing STRUCTURAL fact is not the literal `R(−θ)`: it is that ONE
 * rotation is factored over a SUM — the paper offset and `viewCenter` are
 * rotated by the SAME matrix. A transform that rotates the offset but not the
 * centre displaces the whole rendered and interactive window by
 * `(I − R(θ))·viewCenter`, and because every consumer here shares this one
 * helper, no cross-check between them can expose it (@codex PR #794 r4 P2).
 * Note too that `ezdxf` rotates `viewCenter` even when the target is zero, so
 * this is not a target-only correction: it moves every twisted viewport.
 *
 * ## The SIGN is derived from THIS repo, not ported from ezdxf
 *
 * `ezdxf` rotates the paper offset by `R(−θ)` on the paper→model direction;
 * this repo rotates it by `R(+θ)` — `viewportClipFinalize.paperToViewportModel`,
 * `viewportMembership.computeViewportViewRect`,
 * `scene3d/viewports/viewportTransform2d.paperToModel`,
 * `scene3d/viewports/viewportCullingAdapter.computeViewportModelBounds` and
 * `rendering/viewportRenderPass.buildViewportModelCamera`'s
 * `cam.up = (−sin θ, cos θ)` all do, and group 51 reaches them unmodified
 * (`dxf-io/entityParser` group 51 → `viewTwistAngle`, degrees, no sign flip).
 * So this repo's twist-sign convention is the OPPOSITE of `ezdxf`'s (or it
 * reads group 51 with the other sign — the two are indistinguishable from
 * inside). Whether that convention is itself right is a SEPARATE question and
 * is deliberately not reopened here.
 *
 * What `ezdxf` settles is the STRUCTURE, and the structure forces the sign
 * from internal consistency: the rotation applied to `viewCenter` MUST be the
 * one this repo already applies to the paper offset, because in the algebra
 * above they are one rotation factored over a sum. Hence `R(+θ)` below, and
 * `R(−θ)` in the inverse. Composed at a consumer this gives
 *
 *     model = target + R(θ)·viewCenter + R(θ)·(scale · paperOffset)
 *           = target + R(θ)·(viewCenter + scale · paperOffset)
 *
 * which is `ezdxf`'s relation with θ ↦ −θ. Pinned against ezdxf-generated
 * numbers in `viewportViewFrame.ezdxfOracle.test.ts`.
 *
 * `viewTwistAngle` is OPTIONAL on the source type (defaulting to 0) so
 * `Pick`-shaped literals and pre-twist test stubs keep compiling, and an
 * untwisted viewport stays byte-identical to the plain sum.
 */

/**
 * The fields this reading needs. Structural (not `Pick<ViewportEntity, …>`)
 * so `Pick`-typed callers and test literals that predate `viewTarget` keep
 * compiling — an absent target is the WCS origin, which is exactly the
 * pre-target behaviour.
 */
export interface ViewportViewFrameSource {
  readonly viewCenter: { readonly x: number; readonly y: number };
  readonly viewTarget?: { readonly x: number; readonly y: number } | undefined;
  /**
   * DXF group 51, DEGREES, raw (`dxf-io/entityParser` applies no conversion).
   * OPTIONAL and defaulting to `0` for the same reason `viewTarget` is: an
   * untwisted viewport must stay byte-identical to the plain sum. See the
   * module doc's twist section for why it belongs to this reading at all.
   */
  readonly viewTwistAngle?: number | undefined;
}

/**
 * `R(sign·θ)·(x, y)` with `θ` in DEGREES. `sign` is `+1` for DCS→WCS and `−1`
 * for its inverse; see the module doc for why this repo's `+θ` is the mirror
 * of `ezdxf`'s.
 */
function rotateByTwistDeg(
  x: number,
  y: number,
  twistDeg: number,
  sign: 1 | -1,
): { x: number; y: number } {
  const theta = (sign * twistDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/**
 * The WCS model point at the centre of a viewport's window — `target.xy +
 * R(viewTwistAngle)·(12, 22)`. See the module doc for the DXF derivation, the
 * AutoCAD-2018 measurement that settles the summand, the `?? 0` trap and the
 * `ezdxf` derivation that settles the rotation (and its sign).
 *
 * The `twist === 0` fast path is not an optimisation: it keeps an untwisted
 * viewport BYTE-identical to the plain sum, `-0` included, so the measured
 * untwisted behaviour is provably unchanged.
 */
export function viewportWcsViewCenter(
  viewport: ViewportViewFrameSource,
): { x: number; y: number } {
  const targetX = viewport.viewTarget?.x ?? 0;
  const targetY = viewport.viewTarget?.y ?? 0;
  const twist = viewport.viewTwistAngle ?? 0;
  if (twist === 0) {
    return {
      x: targetX + viewport.viewCenter.x,
      y: targetY + viewport.viewCenter.y,
    };
  }
  const rotated = rotateByTwistDeg(
    viewport.viewCenter.x,
    viewport.viewCenter.y,
    twist,
    1,
  );
  return { x: targetX + rotated.x, y: targetY + rotated.y };
}

/**
 * The exact INVERSE of {@link viewportWcsViewCenter}: given a WCS model point
 * that should become the centre of the viewport's window, the value to STORE
 * in the entity's `viewCenter` field (groups 12/22, in DCS) so that
 * `viewportWcsViewCenter` reads that WCS point back.
 *
 * `viewCenter` is persisted in DCS — the DXF group codes are DCS and this repo
 * keeps them that way, so the Properties palette's "View center X/Y" and the
 * DXF round-trip both stay on the authored numbers. Every value that comes OUT
 * of a viewport transform (`viewportTransform2d.paperToModel`,
 * `viewportClipFinalize.paperToViewportModel`) is therefore in the WRONG frame
 * for that field and must pass through here first. Skipping this un-offset
 * writes `target + wcs` into a DCS slot and moves the view by a whole target
 * vector on every gesture — which then compounds, because the next gesture
 * reads the corrupted value back.
 *
 * # A DELTA is no longer frame-free once the twist is in
 *
 * This doc used to say an incremental write-back (`oldViewCenter + delta`)
 * needs neither helper, because the DCS↔WCS map was a pure TRANSLATION and a
 * difference is translation-invariant. With the twist folded in the map is a
 * rotation as well, the DCS and WCS AXES differ, and a delta is no longer
 * frame-free: `Δstored = R(−θ)·Δwcs`. So a screen-derived pan delta — which
 * `viewportZoomPan.panViewportByPixels` rotates by `R(+θ)` to reach WCS — must
 * now come back through this helper exactly like the wheel-zoom does
 * (`stageInteractions`' `vpRedirect`, which therefore captures the WCS centre
 * at pointerdown). Only a delta that is ALREADY in DCS may skip it.
 */
export function viewportStoredViewCenterFromWcs(
  viewport: Pick<ViewportViewFrameSource, "viewTarget" | "viewTwistAngle">,
  wcsPoint: { readonly x: number; readonly y: number },
): { x: number; y: number } {
  const dx = wcsPoint.x - (viewport.viewTarget?.x ?? 0);
  const dy = wcsPoint.y - (viewport.viewTarget?.y ?? 0);
  const twist = viewport.viewTwistAngle ?? 0;
  // Byte-identical to the pure subtraction when untwisted — see the forward
  // helper's note on why the fast path is a correctness statement.
  if (twist === 0) return { x: dx, y: dy };
  return rotateByTwistDeg(dx, dy, twist, -1);
}
